import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ArrowClockwise,
  HandThumbsDown,
  HandThumbsDownFill,
  HandThumbsUp,
  HandThumbsUpFill,
  InfoCircle,
  SendFill,
} from "react-bootstrap-icons";
import { sendSensaiMessage, submitSensaiFeedback } from "../../services/APIClient";
import {
  buildCsdWorkflowUrl,
  createWorkflowLaunchId,
  getCanonicalWorkflowId,
  getCsdOrigin,
  isEmbeddedWorkflowSupported,
  WORKFLOW_MESSAGE_TARGET,
  WORKFLOW_READY_TIMEOUT_MS,
} from "../../utils/csdWorkflowEmbed";
import "./SensaiPanel.css";

const SUGGESTED_QUESTIONS = [
  "Can you help me create a class?",
  "Can a child retake a task?",
  "When does the screening window open?",
];

const URL_PATTERN = /https?:\/\/[^\s)]+/i;

const CSD_WORKFLOW_ACTIONS = {
  "reset-task": {
    workflow: "reset-task",
    label: "Open Retest Task workflow",
  },
  "add-child": {
    workflow: "add-child",
    label: "Open Add Child workflow",
  },
  "remove-child": {
    workflow: "remove-child",
    label: "Open Remove Child workflow",
  },
  "create-class": {
    workflow: "create-class",
    label: "Open Create Class workflow",
  },
  "remove-class": {
    workflow: "remove-class",
    label: "Open Remove Class workflow",
  },
  "request-access": {
    workflow: "request-access",
    label: "Open Request Access workflow",
  },
  "request-access-other-teacher": {
    workflow: "request-access-other-teacher",
    label: "Open Request Access workflow",
  },
  "swap-students-results": {
    workflow: "swap-students-results",
    label: "Open Swap Students workflow",
  },
  "fix-duplicate-child": {
    workflow: "fix-duplicate-child",
    label: "Open Duplicate Child workflow",
  },
  "fix-duplicate-results": {
    workflow: "fix-duplicate-results",
    label: "Open Duplicate Results workflow",
  },
  "move-transfer-children": {
    workflow: "move-transfer-children",
    label: "Open Move Pupils workflow",
  },
  "update-teacher-roles": {
    workflow: "update-teacher-roles",
    label: "Open Teacher Roles workflow",
  },
  "remove-colleague-access": {
    workflow: "remove-colleague-access",
    label: "Open Remove Colleague Access workflow",
  },
  "remove-teacher-from-school": {
    workflow: "remove-teacher-from-school",
    label: "Open Remove Teacher workflow",
  },
  "invite-teacher-to-platform": {
    workflow: "invite-teacher-to-platform",
    label: "Open Invite Teacher workflow",
  },
  "send-account-setup-link": {
    workflow: "send-account-setup-link",
    label: "Open Account Setup Link workflow",
  },
  "send-password-reset-link": {
    workflow: "send-password-reset-link",
    label: "Open Password Reset workflow",
  },
  "update-user-email": {
    workflow: "update-user-email",
    label: "Open Update Email workflow",
  },
  "rename-class": {
    workflow: "rename-class",
    label: "Open Rename Class workflow",
  },
  "assign-alpaca-lead": {
    workflow: "assign-alpaca-lead",
    label: "Open Assign ALPACA Lead workflow",
  },
  "assign-alpaca-lead-role": {
    workflow: "assign-alpaca-lead",
    label: "Open Assign ALPACA Lead workflow",
  },
  "assign-screener": {
    workflow: "assign-screener",
    label: "Open Assign Screener workflow",
  },
  "extend-trial": {
    workflow: "extend-trial",
    label: "Open Extend Trial workflow",
  },
  "restore-child-results": {
    workflow: "restore-child-results",
    label: "Open Restore Child Results workflow",
  },
  "send-whatsapp-group-invite": {
    workflow: "send-whatsapp-group-invite",
    label: "Open WhatsApp Invite workflow",
  },
  "provide-login-bypass": {
    workflow: "provide-login-bypass",
    label: "Open Login Bypass workflow",
  },
  "activate-teacher-account": {
    workflow: "activate-teacher-account",
    label: "Open Activate Teacher Account workflow",
  },
  "merge-classes": {
    workflow: "merge-classes",
    label: "Open Merge Classes workflow",
  },
  "remove-teacher-assignment": {
    workflow: "remove-teacher-assignment",
    label: "Open Remove Teacher Assignment workflow",
  },
  "request-early-platform-access": {
    workflow: "request-early-platform-access",
    label: "Open Early Platform Access workflow",
  },
  "request-invoice": {
    workflow: "request-invoice",
    label: "Open Request Invoice workflow",
  },
  "trial-access-status": {
    workflow: "trial-access-status",
    label: "Open Trial Access Status workflow",
  },
  "fix-duplicate-child-results": {
    workflow: "fix-duplicate-child-results",
    label: "Open Duplicate Child Results workflow",
  },
  "export-assessment-data": {
    workflow: "export-assessment-data",
    label: "Open Export Assessment Data workflow",
  },
  "make-results-visible": {
    workflow: "make-results-visible",
    label: "Open Make Results Visible workflow",
  },
  "escalate-technical-issue": {
    workflow: "escalate-technical-issue",
    label: "Open Technical Issue workflow",
  },
  "resolve-login-issue": {
    workflow: "resolve-login-issue",
    label: "Open Login Issue workflow",
  },
  "rename-user": {
    workflow: "rename-user",
    label: "Open Rename User workflow",
  },
  "offer-to-add-teacher": {
    workflow: "offer-to-add-teacher",
    label: "Open Add Teacher workflow",
  },
  "offer-meeting": {
    workflow: "offer-meeting",
    label: "Open Request Meeting workflow",
  },
  "screening-guidance-resources": {
    workflow: "screening-guidance-resources",
    label: "Open Screening Guidance workflow",
  },
  "escalate-feature-request": {
    workflow: "escalate-feature-request",
    label: "Open Feature Request workflow",
  },
};

const TEACHER_DASHBOARD_URL_ACTIONS = {
  "/retesttaskdashboard": CSD_WORKFLOW_ACTIONS["reset-task"],
};

const WELCOME_MESSAGE = {
  id: "welcome",
  role: "assistant",
  text:
    "Hi, I'm ALFIE. " +
    "I can help you with questions about the screening window - " +
    "when it opens, when it closes, and whether it is currently active.",
  escalated: false,
  timestamp: new Date().toISOString(),
};

const createWelcomeMessage = (teacherName = "") => {
  const firstName = teacherName.trim().split(/\s+/)[0];

  return {
    ...WELCOME_MESSAGE,
    text:
      `${firstName ? `Hi ${firstName}, ` : "Hi, "}` +
      "I'm ALFIE. I'm here to help make your ALPACA day a little smoother. " +
      "Ask me anything you need help with, and I'll guide you to the right next step.",
    timestamp: new Date().toISOString(),
  };
};

function createSensaiConversationId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `sensai_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function parseJsonString(value) {
  if (typeof value !== "string") {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (err) {
    return null;
  }
}

function normalizeSensaiResponse(response) {
  const body = parseJsonString(response?.body);
  const data = parseJsonString(response?.data) || response?.data;
  const payload = body || data || response || {};

  if (typeof payload === "string") {
    return { response: payload };
  }

  return payload;
}

function getSensaiResponseText(payload) {
  const text =
    payload.reply ||
    payload.response ||
    payload.message ||
    payload.answer ||
    payload.text ||
    payload.output?.text ||
    payload.body ||
    payload.data ||
    "";

  return typeof text === "string" ? text.trim() : "";
}

function getSensaiSessionId(payload) {
  const sessionId = payload.session_id || payload.sessionId;
  return typeof sessionId === "string" && sessionId.trim()
    ? sessionId.trim()
    : "";
}

function getWorkflowPayload(payload, workflowAction = null) {
  if (payload.workflow_payload?.workflow) {
    return payload.workflow_payload;
  }

  if (payload.workflow) {
    return { workflow: payload.workflow, prefill: {} };
  }

  if (workflowAction?.workflow) {
    return { workflow: workflowAction.workflow, prefill: {} };
  }

  return null;
}

function getFirstUrlFromText(text = "") {
  return text.match(URL_PATTERN)?.[0] || "";
}

function stripRawUrlsFromText(text = "") {
  return text
    .replace(/\s*(?:here:?\s*)?https?:\/\/[^\s)]+/gi, ".")
    .replace(/\s+\./g, ".")
    .replace(/\.{2,}/g, ".")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

function getWorkflowActionFromText(text = "") {
  const workflowPatterns = [
    /\bworkflow\s*=\s*([a-z0-9-]+)/i,
    /use this workflow in the UI:\s*([a-z0-9-]+)/i,
  ];

  for (const pattern of workflowPatterns) {
    const workflow = text.match(pattern)?.[1]?.toLowerCase();
    if (workflow) {
      return (
        CSD_WORKFLOW_ACTIONS[workflow] || {
          workflow,
          label: "Open workflow in CSD",
        }
      );
    }
  }

  return null;
}

function stripWorkflowInstructionsFromText(text = "") {
  return text
    .replace(/\s*use this workflow in the UI:\s*[a-z0-9-]+/gi, "")
    .replace(/\s*\bworkflow\s*=\s*[a-z0-9-]+/gi, "")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

function cleanReplyText(text = "") {
  return stripWorkflowInstructionsFromText(stripRawUrlsFromText(text));
}

function getTeacherDashboardActionFromUrl(url) {
  try {
    const { hostname, pathname } = new URL(url);
    const isTeacherDashboardUrl =
      hostname === "teacher.alpaca-assessment.com" ||
      hostname.endsWith(".teacher.alpaca-assessment.com") ||
      hostname === window.location.hostname;

    if (!isTeacherDashboardUrl) return null;

    return TEACHER_DASHBOARD_URL_ACTIONS[pathname] || null;
  } catch (err) {
    return null;
  }
}

function buildSensaiAction(payload, schoolId, replyText, workflowPayload = null) {
  if (workflowPayload?.workflow) {
    return {
      label:
        payload.cta_label ||
        CSD_WORKFLOW_ACTIONS[workflowPayload.workflow]?.label ||
        "Open workflow",
      url: buildCsdWorkflowUrl(workflowPayload, schoolId),
      workflowPayload,
    };
  }

  const workflowAction = getWorkflowActionFromText(replyText);
  const workflowActionPayload = getWorkflowPayload(payload, workflowAction);

  if (workflowActionPayload) {
    return {
      label:
        payload.cta_label ||
        workflowAction?.label ||
        CSD_WORKFLOW_ACTIONS[workflowActionPayload.workflow]?.label ||
        "Open workflow",
      url: buildCsdWorkflowUrl(workflowActionPayload, schoolId),
      workflowPayload: workflowActionPayload,
    };
  }

  const replyUrl = getFirstUrlFromText(replyText);
  const mappedAction = getTeacherDashboardActionFromUrl(replyUrl);

  if (mappedAction) {
    const mappedActionPayload = getWorkflowPayload(payload, mappedAction);

    return {
      label: payload.cta_label || mappedAction.label,
      url: buildCsdWorkflowUrl(mappedActionPayload, schoolId),
      workflowPayload: mappedActionPayload,
    };
  }

  return { label: "", url: "", workflowPayload };
}

function SensaiWorkflowModal({
  fallbackUrl,
  label,
  modalState,
  onClose,
  onFallbackOpen,
  url,
}) {
  if (!url && !fallbackUrl) {
    return null;
  }

  const isFallbackState = modalState === "fallback" || modalState === "error";
  const statusText =
    modalState === "ready"
      ? "Workflow ready"
      : modalState === "error"
      ? "This workflow could not be loaded here."
      : isFallbackState
      ? "This workflow is taking longer than expected."
      : "Loading workflow...";

  return (
    <div
      className="sensai-workflow-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose("cancelled");
        }
      }}
    >
      <div
        className="sensai-workflow-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sensai-workflow-modal-title"
      >
        <div className="sensai-workflow-modal-header">
          <div>
            <h2 id="sensai-workflow-modal-title">{label || "CSD workflow"}</h2>
            <p>{statusText}</p>
          </div>
          <button
            type="button"
            className="sensai-workflow-modal-close"
            onClick={() => onClose("cancelled")}
            aria-label="Close workflow"
          >
            Close
          </button>
        </div>

        <div className="sensai-workflow-modal-body">
          {url && !isFallbackState && (
            <>
              {modalState === "loading" && (
                <div className="sensai-workflow-modal-loading">
                  <span className="sensai-send-spinner" aria-hidden="true" />
                  <span>Opening workflow...</span>
                </div>
              )}
              <iframe
                title={label || "CSD workflow"}
                className="sensai-workflow-frame"
                src={url}
              />
            </>
          )}

          {isFallbackState && (
            <div className="sensai-workflow-fallback">
              <h3>Open this workflow in CSD</h3>
              <p>
                CSD did not confirm that this workflow is ready inside the
                modal. You can open it in CSD and return to this chat afterwards.
              </p>
              {fallbackUrl && (
                <a
                  className="sensai-action-link"
                  href={fallbackUrl}
                  onClick={onFallbackOpen}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in CSD
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * SENSAI chat panel - ALFIE support assistant.
 * Follows the same structural pattern as EOYReportAssistantPanel.
 */
function SensaiPanel({ teacherId, schoolId, teacherName }) {
  const [messages, setMessages] = useState([createWelcomeMessage(teacherName)]);
  const [conversationSessionId, setConversationSessionId] = useState(
    createSensaiConversationId
  );
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [feedbackByMessage, setFeedbackByMessage] = useState({});
  const [feedbackDraftByMessage, setFeedbackDraftByMessage] = useState({});
  const [feedbackStatusByMessage, setFeedbackStatusByMessage] = useState({});
  const [activeWorkflowUrl, setActiveWorkflowUrl] = useState("");
  const [activeWorkflowFallbackUrl, setActiveWorkflowFallbackUrl] = useState("");
  const [activeWorkflowLabel, setActiveWorkflowLabel] = useState("");
  const [activeWorkflowPayload, setActiveWorkflowPayload] = useState(null);
  const [activeWorkflowLaunchId, setActiveWorkflowLaunchId] = useState("");
  const [workflowModalState, setWorkflowModalState] = useState("idle");

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const conversationSessionIdRef = useRef(conversationSessionId);
  const workflowReadyTimeoutRef = useRef(null);

  const updateConversationSessionId = useCallback((sessionId) => {
    conversationSessionIdRef.current = sessionId;
    setConversationSessionId(sessionId);
  }, []);

  const clearWorkflowReadyTimeout = useCallback(() => {
    if (workflowReadyTimeoutRef.current) {
      clearTimeout(workflowReadyTimeoutRef.current);
      workflowReadyTimeoutRef.current = null;
    }
  }, []);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => {
      clearWorkflowReadyTimeout();
    };
  }, [clearWorkflowReadyTimeout]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      setError(null);
      setShowSuggestions(false);

      // Add teacher message immediately
      const teacherMsg = {
        id: `msg_${Date.now()}`,
        role: "teacher",
        text: trimmed,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, teacherMsg]);
      setInputValue("");
      setIsLoading(true);

      try {
        const response = await sendSensaiMessage({
          message: trimmed,
          session_id: conversationSessionIdRef.current,
          teacher_id: teacherId || null,
          school_id: schoolId || null,
          teacher_name: teacherName || null,
        });

        const sensaiPayload = normalizeSensaiResponse(response);
        const canonicalSessionId = getSensaiSessionId(sensaiPayload);
        if (
          canonicalSessionId &&
          canonicalSessionId !== conversationSessionIdRef.current
        ) {
          updateConversationSessionId(canonicalSessionId);
        }

        const rawReplyText = getSensaiResponseText(sensaiPayload);
        const workflowPayload = getWorkflowPayload(sensaiPayload);
        const action = buildSensaiAction(
          sensaiPayload,
          schoolId,
          rawReplyText,
          workflowPayload
        );
        const finalWorkflowPayload = action.workflowPayload || workflowPayload;
        const cleanedReplyText = cleanReplyText(rawReplyText);

        const alfieMsg = {
          id: `msg_${Date.now()}_resp`,
          feedbackId: Date.now(),
          role: "assistant",
          text:
            cleanedReplyText ||
            "I received your request, but I couldn't display the reply properly. Please try again.",
          escalated: sensaiPayload.escalated || false,
          intent: sensaiPayload.intent,
          reason: sensaiPayload.reason,
          workflowPayload: finalWorkflowPayload,
          workflow: finalWorkflowPayload?.workflow || null,
          ctaLabel: action.label,
          actionUrl: action.url,
          referenceNumber:
            sensaiPayload.reference || sensaiPayload.reference_number,
          requestId: sensaiPayload.request_id || sensaiPayload.reference,
          userQuestion: trimmed,
          timestamp: sensaiPayload.timestamp || new Date().toISOString(),
        };
        setMessages((prev) => [...prev, alfieMsg]);
      } catch (err) {
        console.error("[SENSAI] Request failed:", err);
        setError(
          "Something went wrong. Please try again or contact info@alpaca-assessment.com."
        );
        // Remove the teacher message if the request failed
        setMessages((prev) => prev.filter((m) => m.id !== teacherMsg.id));
        setInputValue(trimmed);
      } finally {
        setIsLoading(false);
        // Re-focus input after response
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    },
    [isLoading, teacherId, schoolId, teacherName, updateConversationSessionId]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleSuggestionClick = (question) => {
    sendMessage(question);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleNewChat = () => {
    updateConversationSessionId(createSensaiConversationId());
    setMessages([createWelcomeMessage(teacherName)]);
    setInputValue("");
    setError(null);
    setShowSuggestions(true);
    inputRef.current?.focus();
  };

  const submitFeedback = async (msg, rating, comment = "") => {
    setFeedbackStatusByMessage((prev) => ({ ...prev, [msg.id]: "saving" }));
    try {
      await submitSensaiFeedback({
        reference: msg.referenceNumber || msg.requestId || "",
        rating,
        reason: comment,
        message: msg.userQuestion || "",
        reply: msg.text,
      });
      setFeedbackStatusByMessage((prev) => ({ ...prev, [msg.id]: "saved" }));
    } catch (err) {
      console.error("[SENSAI] Feedback failed:", err);
      setFeedbackStatusByMessage((prev) => ({ ...prev, [msg.id]: "error" }));
    }
  };

  const handleFeedback = async (msg, rating) => {
    setFeedbackByMessage((prev) => ({ ...prev, [msg.id]: rating }));
    if (rating === "up") {
      setFeedbackDraftByMessage((prev) => ({ ...prev, [msg.id]: "" }));
      await submitFeedback(msg, "up");
    }
  };

  const handleFeedbackReasonChange = (msgId, value) => {
    setFeedbackDraftByMessage((prev) => ({ ...prev, [msgId]: value }));
  };

  const handleFeedbackReasonSubmit = async (msg) => {
    const comment = (feedbackDraftByMessage[msg.id] || "").trim();
    await submitFeedback(msg, "down", comment);
  };

  const resetWorkflowModal = useCallback(() => {
    setActiveWorkflowUrl("");
    setActiveWorkflowFallbackUrl("");
    setActiveWorkflowLabel("");
    setActiveWorkflowPayload(null);
    setActiveWorkflowLaunchId("");
    setWorkflowModalState("idle");
  }, []);

  const closeWorkflowModal = useCallback(
    (status = "cancelled") => {
      if (workflowModalState === "idle") {
        return;
      }

      clearWorkflowReadyTimeout();
      resetWorkflowModal();

      if (status === "completed" || status === "cancelled") {
        const text =
          status === "completed"
            ? "That workflow is complete. You're back in SENSAI."
            : "No problem, I closed that workflow. You're back in SENSAI.";

        setMessages((prev) => [
          ...prev,
          {
            id: `msg_${Date.now()}_workflow_${status}`,
            role: "assistant",
            text,
            escalated: false,
            timestamp: new Date().toISOString(),
          },
        ]);
      }
    },
    [
      clearWorkflowReadyTimeout,
      resetWorkflowModal,
      workflowModalState,
    ]
  );

  const openWorkflowModal = (msg) => {
    const workflowPayload = msg.workflowPayload;
    const launchId = createWorkflowLaunchId();
    const fallbackUrl =
      msg.actionUrl || buildCsdWorkflowUrl(workflowPayload, schoolId);
    const embedUrl = buildCsdWorkflowUrl(workflowPayload, schoolId, {
      embed: true,
      launchId,
      parentOrigin: window.location.origin,
    });

    localStorage.setItem("td_return_url", window.location.href);
    clearWorkflowReadyTimeout();

    setActiveWorkflowUrl(embedUrl);
    setActiveWorkflowFallbackUrl(fallbackUrl);
    setActiveWorkflowLabel(msg.ctaLabel || "Open workflow");
    setActiveWorkflowPayload(workflowPayload);
    setActiveWorkflowLaunchId(launchId);
    setWorkflowModalState(embedUrl ? "loading" : "fallback");

    if (embedUrl) {
      workflowReadyTimeoutRef.current = setTimeout(() => {
        setWorkflowModalState((currentState) =>
          currentState === "loading" ? "fallback" : currentState
        );
      }, WORKFLOW_READY_TIMEOUT_MS);
    }
  };

  const handleCsdActionClick = () => {
    localStorage.setItem("td_return_url", window.location.href);
  };

  useEffect(() => {
    if (!activeWorkflowLaunchId || workflowModalState === "idle") {
      return undefined;
    }

    const expectedOrigin = getCsdOrigin();

    const handleWorkflowMessage = (event) => {
      if (!expectedOrigin || event.origin !== expectedOrigin) {
        return;
      }

      const data = event.data || {};
      const workflow = getCanonicalWorkflowId(activeWorkflowPayload?.workflow);

      if (
        data.source !== "csd" ||
        data.target !== WORKFLOW_MESSAGE_TARGET ||
        data.workflow !== workflow ||
        data.launchId !== activeWorkflowLaunchId
      ) {
        return;
      }

      if (data.type === "workflow.ready") {
        clearWorkflowReadyTimeout();
        setWorkflowModalState("ready");
        return;
      }

      if (data.type === "workflow.completed") {
        closeWorkflowModal("completed");
        return;
      }

      if (data.type === "workflow.cancelled") {
        closeWorkflowModal("cancelled");
        return;
      }

      if (data.type === "workflow.error") {
        clearWorkflowReadyTimeout();
        setWorkflowModalState("error");
      }
    };

    window.addEventListener("message", handleWorkflowMessage);
    return () => window.removeEventListener("message", handleWorkflowMessage);
  }, [
    activeWorkflowLaunchId,
    activeWorkflowPayload,
    clearWorkflowReadyTimeout,
    closeWorkflowModal,
    workflowModalState,
  ]);

  return (
    <div className="sensai-panel-root">
      {/* Header */}
      <div className="sensai-header">
        <div className="sensai-header-left">
          <div className="sensai-assistant-mark" aria-hidden="true">
            <img
              alt=""
              className="sensai-assistant-mark-img"
              src="/assets/imgs/Alpaca-front-01.png"
            />
          </div>
          <div>
            <h1 className="sensai-title">SENSAI</h1>
            <p className="sensai-subtitle">
              ALPACA Support Assistant - powered by ALFIE
            </p>
          </div>
        </div>
        <button
          type="button"
          className="sensai-new-chat-btn"
          onClick={handleNewChat}
          title="Start a new conversation"
        >
          <ArrowClockwise size={14} aria-hidden="true" />
          <span>New chat</span>
        </button>
      </div>

      {/* Chat window */}
      <div className="sensai-chat-window">
        <div className="sensai-messages">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`sensai-message-row sensai-message-row--${msg.role}`}
            >
              {msg.role === "assistant" && (
                <div
                  className="sensai-bubble-avatar sensai-bubble-avatar--assistant"
                  aria-hidden="true"
                >
                  <img
                    alt=""
                    className="sensai-bubble-avatar-img"
                    src="/assets/imgs/Alpaca-front-01.png"
                  />
                </div>
              )}
              <div
                className={`sensai-bubble ${
                  msg.role === "teacher"
                    ? "sensai-bubble--teacher"
                    : msg.escalated
                    ? "sensai-bubble--escalation"
                    : "sensai-bubble--assistant"
                }`}
              >
                {msg.escalated && (
                  <div className="sensai-escalation-label">
                    <InfoCircle size={14} aria-hidden="true" />
                    Support follow-up
                  </div>
                )}
                <p className="sensai-bubble-text">{msg.text}</p>
                {msg.role === "assistant" &&
                  msg.actionUrl &&
                  isEmbeddedWorkflowSupported(msg.workflowPayload?.workflow) && (
                    <button
                      type="button"
                      className="sensai-action-link sensai-action-button"
                      onClick={() => openWorkflowModal(msg)}
                    >
                      {msg.ctaLabel || "Open link"}
                    </button>
                  )}
                {msg.role === "assistant" &&
                  msg.actionUrl &&
                  !isEmbeddedWorkflowSupported(msg.workflowPayload?.workflow) && (
                  <a
                    className="sensai-action-link"
                    href={msg.actionUrl}
                    onClick={handleCsdActionClick}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {msg.ctaLabel || "Open link"}
                  </a>
                )}
                {msg.role === "assistant" && msg.referenceNumber && (
                  <div className="sensai-reference">
                    Ref: {msg.referenceNumber}
                  </div>
                )}
                {msg.role === "assistant" && msg.id !== "welcome" && (
                  <div className="sensai-feedback-wrap">
                    <div className="sensai-feedback">
                      <button
                        type="button"
                        className={`sensai-feedback-btn ${
                          feedbackByMessage[msg.id] === "up" ? "is-selected" : ""
                        }`}
                        onClick={() => handleFeedback(msg, "up")}
                        aria-label="Helpful response"
                        title="Helpful"
                      >
                        {feedbackByMessage[msg.id] === "up" ? (
                          <HandThumbsUpFill size={16} aria-hidden="true" />
                        ) : (
                          <HandThumbsUp size={16} aria-hidden="true" />
                        )}
                      </button>
                      <button
                        type="button"
                        className={`sensai-feedback-btn ${
                          feedbackByMessage[msg.id] === "down" ? "is-selected" : ""
                        }`}
                        onClick={() => handleFeedback(msg, "down")}
                        aria-label="Not helpful response"
                        title="Not helpful"
                      >
                        {feedbackByMessage[msg.id] === "down" ? (
                          <HandThumbsDownFill size={16} aria-hidden="true" />
                        ) : (
                          <HandThumbsDown size={16} aria-hidden="true" />
                        )}
                      </button>
                      {feedbackStatusByMessage[msg.id] === "saved" && (
                        <span className="sensai-feedback-status">Thanks</span>
                      )}
                    </div>
                    {feedbackByMessage[msg.id] === "down" && (
                      <div className="sensai-feedback-reason">
                        <textarea
                          className="sensai-feedback-textarea"
                          value={feedbackDraftByMessage[msg.id] || ""}
                          onChange={(e) =>
                            handleFeedbackReasonChange(msg.id, e.target.value)
                          }
                          placeholder="What went wrong?"
                          rows={2}
                        />
                        <button
                          type="button"
                          className="sensai-feedback-submit"
                          onClick={() => handleFeedbackReasonSubmit(msg)}
                          disabled={feedbackStatusByMessage[msg.id] === "saving"}
                        >
                          Send feedback
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {msg.role === "teacher" && (
                <div className="sensai-bubble-avatar sensai-bubble-avatar--teacher">
                  T
                </div>
              )}
            </div>
          ))}

          {/* Typing indicator */}
          {isLoading && (
            <div className="sensai-message-row sensai-message-row--assistant">
              <div
                className="sensai-bubble-avatar sensai-bubble-avatar--assistant"
                aria-hidden="true"
              >
                <img
                  alt=""
                  className="sensai-bubble-avatar-img"
                  src="/assets/imgs/Alpaca-front-01.png"
                />
              </div>
              <div className="sensai-bubble sensai-bubble--assistant sensai-bubble--typing">
                <span className="sensai-typing-dot" />
                <span className="sensai-typing-dot" />
                <span className="sensai-typing-dot" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggested questions - shown only at start */}
        {showSuggestions && !isLoading && (
          <div className="sensai-suggestions">
            <p className="sensai-suggestions-label">Suggested questions</p>
            <div className="sensai-suggestions-list">
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  type="button"
                  key={q}
                  className="sensai-suggestion-chip"
                  onClick={() => handleSuggestionClick(q)}
                  disabled={isLoading}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="sensai-error" role="alert">
          {error}
        </div>
      )}

      {/* Input bar */}
      <form className="sensai-input-bar" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          className="sensai-input"
          placeholder="Ask ALFIE what you need help with..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          maxLength={500}
          aria-label="Type your question"
        />
        <button
          type="submit"
          className="sensai-send-btn"
          disabled={isLoading || !inputValue.trim()}
          aria-label="Send message"
        >
          {isLoading ? (
            <span className="sensai-send-spinner" />
          ) : (
            <SendFill className="sensai-send-icon" size={17} aria-hidden="true" />
          )}
        </button>
      </form>

      {/* Footer note */}
      <p className="sensai-footer-note">
        ALFIE responses are AI-generated and should be reviewed before acting.
        For support, contact{" "}
        <a href="mailto:info@alpaca-assessment.com">
          info@alpaca-assessment.com
        </a>
      </p>

      {workflowModalState !== "idle" && (
        <SensaiWorkflowModal
          fallbackUrl={activeWorkflowFallbackUrl}
          label={activeWorkflowLabel}
          modalState={workflowModalState}
          onClose={closeWorkflowModal}
          onFallbackOpen={handleCsdActionClick}
          url={activeWorkflowUrl}
        />
      )}
    </div>
  );
}

export default SensaiPanel;
